// NATAMI — Usuarios de equipo (dueño) + página para aceptar invitación.
// Backend: src/server/teamRoutes.ts / teamAccess.ts.

import { useEffect, useRef, useState } from 'react'
import { useNavigate, useParams, Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useAuth } from '@clerk/clerk-react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Users, UserPlus, Trash2, Mail, ArrowLeft, Check } from 'lucide-react'
import { apiFetch } from '../utils/api'

const hasClerk = !!import.meta.env.VITE_CLERK_PUBLISHABLE_KEY

type Role = 'MANAGER' | 'VIEWER'
interface Member {
  id: string
  email: string
  role: Role
  status: 'PENDING' | 'ACTIVE'
  invitedAt: string
  acceptedAt: string | null
  expired: boolean
}
interface TeamResponse {
  owner: { email: string; name: string | null }
  members: Member[]
  seats: { used: number; limit: number }
}

function roleLabel(role: Role, isEs: boolean) {
  if (role === 'MANAGER') return isEs ? 'Administrador de obra' : 'Project manager'
  return isEs ? 'Solo lectura' : 'View only'
}
function roleHelp(role: Role, isEs: boolean) {
  if (role === 'MANAGER') return isEs ? 'Crea y edita en todos los proyectos. No ve facturación ni el equipo.' : 'Creates and edits in every project. No billing or team access.'
  return isEs ? 'Ve todo, no puede cambiar nada.' : 'Sees everything, can’t change anything.'
}

/** Mensaje claro por código del servidor — nunca el código crudo. */
function teamErrorMessage(code: string | undefined, status: number, isEs: boolean, extra?: any): string {
  switch (code) {
    case 'TEAM_SEAT_LIMIT':
      return isEs
        ? `Tu plan incluye ${extra?.seats?.limit ?? ''} usuarios (contándote a ti) y ya están ocupados. Quita a alguien o sube de plan.`
        : `Your plan includes ${extra?.seats?.limit ?? ''} users (including you) and they're all taken. Remove someone or upgrade.`
    case 'TEAM_ALREADY_INVITED': return isEs ? 'Ese correo ya está en tu equipo o tiene una invitación pendiente.' : 'That email is already on your team or has a pending invite.'
    case 'TEAM_CANNOT_INVITE_SELF': return isEs ? 'No puedes invitarte a ti misma(o).' : 'You can’t invite yourself.'
    case 'TEAM_INVALID_INVITE': return isEs ? 'Revisa el correo.' : 'Check the email address.'
    case 'TEAM_INVITE_NOT_FOUND': return isEs ? 'Esta invitación ya no es válida (se usó, se canceló o se reenvió una nueva). Pide que te la manden otra vez.' : 'This invitation is no longer valid (used, cancelled, or replaced). Ask for a new one.'
    case 'TEAM_INVITE_EXPIRED': return isEs ? 'La invitación venció. Pide que te la reenvíen.' : 'The invitation expired. Ask for it to be resent.'
    case 'TEAM_EMAIL_MISMATCH': return isEs ? `La invitación es para ${extra?.invitedEmail ?? 'otro correo'}. Cierra sesión y entra (o crea tu cuenta) con ese correo.` : `This invitation is for ${extra?.invitedEmail ?? 'another email'}. Sign out and sign in (or sign up) with that email.`
    case 'TEAM_ALREADY_MEMBER': return isEs ? 'Tu cuenta ya pertenece a otro equipo. Sal de ese equipo primero.' : 'Your account already belongs to another team. Leave it first.'
    case 'TEAM_ACCOUNT_HAS_OWN_DATA': return isEs ? 'Esta cuenta ya tiene proyectos, suscripción o equipo propios. Usa otro correo para unirte al equipo, o escríbenos para ayudarte.' : 'This account already has its own projects, subscription or team. Use a different email to join, or contact us for help.'
    case 'TEAM_ACCOUNT_NOT_READY': return isEs ? 'Tu cuenta aún se está creando. Espera unos segundos y vuelve a intentar.' : 'Your account is still being set up. Wait a few seconds and try again.'
    case 'TEAM_OWNER_ONLY': return isEs ? 'Solo el dueño de la cuenta puede administrar el equipo.' : 'Only the account owner can manage the team.'
  }
  if (status === 401) return isEs ? 'Tu sesión expiró. Vuelve a iniciar sesión.' : 'Your session expired. Sign in again.'
  return isEs ? 'No se pudo completar. Intenta de nuevo en un momento.' : 'Couldn’t complete that. Try again in a moment.'
}

async function readError(r: Response, isEs: boolean): Promise<string> {
  const body = await r.json().catch(() => null)
  return teamErrorMessage(body?.code, r.status, isEs, body)
}

function useAuthedFetch() {
  const auth = hasClerk ? useAuth() : null
  return async (path: string, init?: RequestInit) => {
    const token = auth ? await auth.getToken() : null
    return apiFetch(path, {
      ...init,
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
        ...(init?.headers || {}),
      },
    })
  }
}

// ── Página del dueño: /team ──────────────────────────────────────────────────
export default function TeamModule() {
  const { i18n } = useTranslation()
  const isEs = i18n.language === 'es'
  const navigate = useNavigate()
  const authedFetch = useAuthedFetch()
  const qc = useQueryClient()
  const [email, setEmail] = useState('')
  const [role, setRole] = useState<Role>('MANAGER')
  const [error, setError] = useState<string | null>(null)
  const [notice, setNotice] = useState<string | null>(null)
  const [confirmRemove, setConfirmRemove] = useState<string | null>(null)

  // Un miembro (no dueño) ve su estado, no la administración.
  const me = useQuery({
    queryKey: ['team-me'],
    queryFn: async () => {
      const r = await authedFetch('/api/team/me')
      if (!r.ok) throw new Error(await readError(r, isEs))
      return r.json() as Promise<{ membership: { role: Role; ownerName: string | null; company: string | null } | null }>
    },
  })
  const isMember = !!me.data?.membership

  const team = useQuery({
    queryKey: ['team'],
    enabled: me.isSuccess && !isMember,
    queryFn: async () => {
      const r = await authedFetch('/api/team')
      if (!r.ok) throw new Error(await readError(r, isEs))
      return r.json() as Promise<TeamResponse>
    },
  })

  const invite = useMutation({
    mutationFn: async () => {
      const r = await authedFetch('/api/team/invites', { method: 'POST', body: JSON.stringify({ email: email.trim(), role, lang: isEs ? 'es' : 'en' }) })
      if (!r.ok) throw new Error(await readError(r, isEs))
      return r.json() as Promise<{ emailSent: boolean }>
    },
    onMutate: () => { setError(null); setNotice(null) },
    onSuccess: (d) => {
      setNotice(d.emailSent
        ? (isEs ? `Invitación enviada a ${email.trim()}.` : `Invitation sent to ${email.trim()}.`)
        : (isEs ? 'La invitación se guardó, pero el correo no salió. Usa "Reenviar" en un momento.' : 'Invitation saved, but the email didn’t go out. Use "Resend" in a moment.'))
      setEmail('')
      qc.invalidateQueries({ queryKey: ['team'] })
    },
    onError: (e: Error) => setError(e.message),
  })

  const act = useMutation({
    mutationFn: async (a: { kind: 'resend' | 'role' | 'remove'; id: string; role?: Role }) => {
      const path = a.kind === 'resend' ? `/api/team/members/${a.id}/resend` : `/api/team/members/${a.id}`
      const method = a.kind === 'resend' ? 'POST' : a.kind === 'role' ? 'PATCH' : 'DELETE'
      const body = a.kind === 'resend' ? { lang: isEs ? 'es' : 'en' } : a.kind === 'role' ? { role: a.role } : undefined
      const r = await authedFetch(path, { method, body: body ? JSON.stringify(body) : undefined })
      if (!r.ok) throw new Error(await readError(r, isEs))
      return { kind: a.kind, data: await r.json().catch(() => ({})) }
    },
    onMutate: () => { setError(null); setNotice(null) },
    onSuccess: ({ kind, data }) => {
      if (kind === 'resend') setNotice(data?.emailSent === false ? (isEs ? 'No se pudo enviar el correo. Intenta otra vez.' : 'The email could not be sent. Try again.') : (isEs ? 'Invitación reenviada.' : 'Invitation resent.'))
      if (kind === 'remove') setNotice(isEs ? 'Acceso quitado.' : 'Access removed.')
      setConfirmRemove(null)
      qc.invalidateQueries({ queryKey: ['team'] })
    },
    onError: (e: Error) => setError(e.message),
  })

  const leave = useMutation({
    mutationFn: async () => {
      const r = await authedFetch('/api/team/leave', { method: 'POST' })
      if (!r.ok) throw new Error(await readError(r, isEs))
    },
    onSuccess: () => { qc.clear(); navigate('/projects') },
    onError: (e: Error) => setError(e.message),
  })

  const seats = team.data?.seats
  const full = seats ? seats.used >= seats.limit : false

  return (
    <div className="p-4 sm:p-5 max-w-3xl mx-auto space-y-4">
      <button onClick={() => navigate('/projects')} className="flex items-center gap-1.5 min-h-[44px] text-[12px] font-[700] text-[#9E9890] hover:text-[#1C1A17]">
        <ArrowLeft size={14} /> {isEs ? 'Mis proyectos' : 'My projects'}
      </button>
      <div className="flex items-center gap-2">
        <Users size={20} className="text-[#283BF9]" />
        <h1 className="text-[22px] font-[900] tracking-[-0.5px] text-[#1C1A17]">{isEs ? 'Equipo' : 'Team'}</h1>
      </div>

      {error && <div role="alert" className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-[13px] font-[600] text-red-700">{error}</div>}
      {notice && <div role="status" className="rounded-xl border border-green-200 bg-green-50 px-4 py-3 text-[13px] font-[600] text-green-800">{notice}</div>}

      {(me.isLoading || (team.isLoading && !isMember)) && <p className="text-[13px] text-[#9E9890]">{isEs ? 'Cargando…' : 'Loading…'}</p>}
      {me.isError && <div role="alert" className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-[13px] font-[600] text-red-700">{(me.error as Error).message}</div>}
      {team.isError && <div role="alert" className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-[13px] font-[600] text-red-700">{(team.error as Error).message}</div>}

      {isMember && me.data?.membership && (
        <div className="rounded-2xl border border-[#E8E4DC] bg-white p-4 space-y-3">
          <p className="text-[14px] text-[#1C1A17]">
            {isEs ? 'Eres parte del equipo de ' : 'You’re on the team of '}
            <strong>{me.data.membership.company || me.data.membership.ownerName}</strong>
            {' · '}{roleLabel(me.data.membership.role, isEs)}
          </p>
          <p className="text-[12px] text-[#6B6560]">{roleHelp(me.data.membership.role, isEs)}</p>
          <button
            onClick={() => leave.mutate()}
            disabled={leave.isPending}
            className="min-h-[44px] px-4 rounded-full border border-[#E8E4DC] text-[13px] font-[700] text-[#1C1A17] disabled:opacity-50"
          >
            {isEs ? 'Salir de este equipo' : 'Leave this team'}
          </button>
        </div>
      )}

      {team.data && (
        <>
          <div className="rounded-2xl border border-[#E8E4DC] bg-white p-4 space-y-3">
            <div className="flex items-center justify-between gap-2 flex-wrap">
              <h2 className="text-[15px] font-[800] text-[#1C1A17]">{isEs ? 'Invitar a alguien' : 'Invite someone'}</h2>
              <span data-testid="team-seats" className="text-[12px] font-[700] text-[#6B6560]">
                {isEs ? `${seats!.used} de ${seats!.limit} usuarios` : `${seats!.used} of ${seats!.limit} users`}
              </span>
            </div>
            <form
              className="flex flex-col sm:flex-row gap-2"
              onSubmit={(e) => { e.preventDefault(); if (email.trim() && !full) invite.mutate() }}
            >
              <label className="sr-only" htmlFor="team-invite-email">{isEs ? 'Correo' : 'Email'}</label>
              <input
                id="team-invite-email"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder={isEs ? 'correo@empresa.com' : 'name@company.com'}
                className="min-h-[44px] flex-1 min-w-0 rounded-xl border border-[#E8E4DC] px-3 text-[14px]"
              />
              <label className="sr-only" htmlFor="team-invite-role">{isEs ? 'Rol' : 'Role'}</label>
              <select
                id="team-invite-role"
                value={role}
                onChange={(e) => setRole(e.target.value as Role)}
                className="min-h-[44px] rounded-xl border border-[#E8E4DC] px-3 text-[14px] bg-white"
              >
                <option value="MANAGER">{roleLabel('MANAGER', isEs)}</option>
                <option value="VIEWER">{roleLabel('VIEWER', isEs)}</option>
              </select>
              <button
                type="submit"
                disabled={invite.isPending || full || !email.trim()}
                className="min-h-[44px] inline-flex items-center justify-center gap-1.5 rounded-full bg-[#283BF9] px-4 text-[13px] font-[800] text-white disabled:opacity-50"
              >
                <UserPlus size={14} /> {invite.isPending ? (isEs ? 'Enviando…' : 'Sending…') : (isEs ? 'Invitar' : 'Invite')}
              </button>
            </form>
            <p className="text-[12px] text-[#6B6560]">{roleHelp(role, isEs)}</p>
            {full && (
              <p className="text-[12px] font-[600] text-[#6B6560]">
                {isEs ? 'Ya usas todos los lugares de tu plan. ' : 'You’re using every seat in your plan. '}
                <Link to="/subscription" className="text-[#283BF9] underline">{isEs ? 'Ver planes' : 'See plans'}</Link>
              </p>
            )}
          </div>

          <div className="rounded-2xl border border-[#E8E4DC] bg-white divide-y divide-[#F2F0EB]">
            <div className="flex items-center gap-3 p-4">
              <div className="min-w-0 flex-1">
                <p className="truncate text-[14px] font-[700] text-[#1C1A17]">{team.data.owner.name || team.data.owner.email}</p>
                <p className="truncate text-[12px] text-[#6B6560]">{team.data.owner.email}</p>
              </div>
              <span className="text-[12px] font-[800] text-[#283BF9]">{isEs ? 'Dueño' : 'Owner'}</span>
            </div>
            {team.data.members.length === 0 && (
              <p className="p-4 text-[13px] text-[#9E9890]">{isEs ? 'Todavía no has invitado a nadie.' : 'You haven’t invited anyone yet.'}</p>
            )}
            {team.data.members.map((m) => (
              <div key={m.id} className="p-4 space-y-2">
                <div className="flex items-center gap-3">
                  <div className="min-w-0 flex-1">
                    <p className="break-all text-[14px] font-[700] text-[#1C1A17]">{m.email}</p>
                    <p className="text-[12px] text-[#6B6560]">
                      {m.status === 'ACTIVE'
                        ? (isEs ? 'Activo' : 'Active')
                        : m.expired ? (isEs ? 'Invitación vencida' : 'Invitation expired') : (isEs ? 'Invitación pendiente' : 'Invitation pending')}
                    </p>
                  </div>
                  <select
                    aria-label={isEs ? 'Rol' : 'Role'}
                    value={m.role}
                    disabled={act.isPending}
                    onChange={(e) => act.mutate({ kind: 'role', id: m.id, role: e.target.value as Role })}
                    className="min-h-[44px] max-w-[46%] shrink-0 rounded-xl border border-[#E8E4DC] px-2 text-[13px] bg-white"
                  >
                    <option value="MANAGER">{roleLabel('MANAGER', isEs)}</option>
                    <option value="VIEWER">{roleLabel('VIEWER', isEs)}</option>
                  </select>
                </div>
                <div className="flex flex-wrap gap-2">
                  {m.status === 'PENDING' && (
                    <button
                      onClick={() => act.mutate({ kind: 'resend', id: m.id })}
                      disabled={act.isPending}
                      className="min-h-[44px] inline-flex items-center gap-1.5 rounded-full border border-[#E8E4DC] px-3 text-[12px] font-[700] text-[#1C1A17] disabled:opacity-50"
                    >
                      <Mail size={13} /> {isEs ? 'Reenviar' : 'Resend'}
                    </button>
                  )}
                  {confirmRemove === m.id ? (
                    <>
                      <button
                        onClick={() => act.mutate({ kind: 'remove', id: m.id })}
                        disabled={act.isPending}
                        className="min-h-[44px] inline-flex items-center gap-1.5 rounded-full bg-red-600 px-3 text-[12px] font-[800] text-white disabled:opacity-50"
                      >
                        <Check size={13} /> {isEs ? 'Sí, quitar acceso' : 'Yes, remove access'}
                      </button>
                      <button onClick={() => setConfirmRemove(null)} className="min-h-[44px] rounded-full px-3 text-[12px] font-[700] text-[#6B6560]">
                        {isEs ? 'Cancelar' : 'Cancel'}
                      </button>
                    </>
                  ) : (
                    <button
                      onClick={() => setConfirmRemove(m.id)}
                      className="min-h-[44px] inline-flex items-center gap-1.5 rounded-full border border-[#E8E4DC] px-3 text-[12px] font-[700] text-red-700"
                    >
                      <Trash2 size={13} /> {m.status === 'PENDING' ? (isEs ? 'Cancelar invitación' : 'Cancel invitation') : (isEs ? 'Quitar acceso' : 'Remove access')}
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  )
}

// ── Aceptar invitación: /invite/:token ───────────────────────────────────────
export function InviteAcceptPage() {
  const { token = '' } = useParams()
  const { i18n } = useTranslation()
  const isEs = i18n.language === 'es'
  const navigate = useNavigate()
  const auth = hasClerk ? useAuth() : null
  const authedFetch = useAuthedFetch()
  const qc = useQueryClient()
  const [state, setState] = useState<'idle' | 'working' | 'done' | 'error'>('idle')
  const [message, setMessage] = useState<string | null>(null)
  const started = useRef(false)

  const signedIn = !hasClerk || !!auth?.isSignedIn
  const loaded = !hasClerk || !!auth?.isLoaded
  const here = `/invite/${encodeURIComponent(token)}`

  const accept = async (attempt = 0): Promise<void> => {
    setState('working'); setMessage(null)
    try {
      const r = await authedFetch('/api/team/invites/accept', { method: 'POST', body: JSON.stringify({ token }) })
      if (!r.ok) {
        const body = await r.clone().json().catch(() => null)
        // Recién creada la cuenta, el registro tarda unos segundos en llegar.
        if (body?.code === 'TEAM_ACCOUNT_NOT_READY' && attempt < 4) {
          await new Promise((res) => setTimeout(res, 2500))
          return accept(attempt + 1)
        }
        setState('error'); setMessage(await readError(r, isEs)); return
      }
      setState('done')
      qc.clear()
      setTimeout(() => navigate('/projects'), 1200)
    } catch {
      setState('error')
      setMessage(isEs ? 'No hay conexión. Revisa tu internet e intenta otra vez.' : 'No connection. Check your internet and try again.')
    }
  }

  useEffect(() => {
    if (loaded && signedIn && !started.current) { started.current = true; void accept() }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loaded, signedIn])

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#F7F5F0] p-4">
      <div className="w-full max-w-md rounded-2xl bg-white border border-[#E8E4DC] p-6 space-y-4 text-center">
        <img src="/natami_logo.png" alt="NATAMI" className="h-7 mx-auto" />
        <h1 className="text-[18px] font-[900] text-[#1C1A17]">{isEs ? 'Invitación a un equipo' : 'Team invitation'}</h1>
        {!loaded && <p className="text-[13px] text-[#9E9890]">{isEs ? 'Cargando…' : 'Loading…'}</p>}
        {loaded && !signedIn && (
          <>
            <p className="text-[14px] text-[#6B6560]">
              {isEs ? 'Inicia sesión o crea tu cuenta con el correo al que llegó la invitación.' : 'Sign in or create your account with the email the invitation was sent to.'}
            </p>
            <div className="flex flex-col gap-2">
              <Link to={`/sign-in?redirect_url=${encodeURIComponent(here)}`} className="min-h-[44px] inline-flex items-center justify-center rounded-full bg-[#283BF9] px-4 text-[14px] font-[800] text-white">
                {isEs ? 'Iniciar sesión' : 'Sign in'}
              </Link>
              <Link to={`/sign-up?redirect_url=${encodeURIComponent(here)}`} className="min-h-[44px] inline-flex items-center justify-center rounded-full border border-[#E8E4DC] px-4 text-[14px] font-[800] text-[#1C1A17]">
                {isEs ? 'Crear cuenta' : 'Create account'}
              </Link>
            </div>
          </>
        )}
        {state === 'working' && <p className="text-[14px] text-[#6B6560]">{isEs ? 'Aceptando invitación…' : 'Accepting invitation…'}</p>}
        {state === 'done' && <p role="status" className="text-[14px] font-[700] text-green-700">{isEs ? '¡Listo! Ya eres parte del equipo.' : 'Done! You’re on the team.'}</p>}
        {state === 'error' && (
          <>
            <p role="alert" className="text-[14px] font-[600] text-red-700">{message}</p>
            <button onClick={() => void accept()} className="min-h-[44px] rounded-full border border-[#E8E4DC] px-4 text-[13px] font-[700]">
              {isEs ? 'Intentar de nuevo' : 'Try again'}
            </button>
          </>
        )}
      </div>
    </div>
  )
}
